import * as THREE from 'three';
import type { V2 } from '../../core/math';
import { RoomKit } from './backRoomScene';

/**
 * Sid's hut in Gorringe's yard: a soot-brick lean-to with a rusty corrugated roof, a green plank door
 * under a caged lamp, a boarded window and a pile of tyres. The player walks up to the door (where Sid
 * used to stand with his tea chest); E goes in. The hut stands behind that spot, away from the road, and
 * has a static collider so nobody walks through it (offline and on the client online: it is part of
 * the weapons module, not the server's city).
 */

export const HUT = {
  width: 4.2,
  depth: 3.0,
  height: 2.6,
  /** Door spot → front wall (m). */
  gap: 1.3,
  /** The door sits this far along the front wall from its middle (hut-local X, m): the hut stands off to one side, clear of the pavement. */
  doorX: -1.3,
} as const;

/**
 * The way the door faces: +Z, toward the street camera (screen up is north, docs/ARCHITECTURE.md), so
 * the door, its lamp and the sign read from above.
 */
export const DOOR_FACING = 0;

/** Where the hut stands for a door spot (the player stands there) and the way the door faces (heading, the game's convention). */
export function hutPlacement(door: V2, heading = DOOR_FACING): { center: V2; angle: number } {
  const dx = Math.sin(heading);
  const dz = Math.cos(heading);
  // Hut-local +X in the world (three's rotation.y = heading).
  const rx = Math.cos(heading);
  const rz = -Math.sin(heading);
  const back = HUT.gap + HUT.depth / 2;
  return { center: { x: door.x - dx * back - rx * HUT.doorX, z: door.z - dz * back - rz * HUT.doorX }, angle: heading };
}

function signTexture(): THREE.CanvasTexture | null {
  if (typeof document === 'undefined') return null;
  const c = document.createElement('canvas');
  c.width = 256;
  c.height = 64;
  const g = c.getContext('2d');
  if (!g) return null;
  g.fillStyle = '#e2d6b2';
  g.fillRect(0, 0, 256, 64);
  g.fillStyle = '#7a1c12';
  g.textAlign = 'center';
  g.font = "bold 34px 'BH Anton', Impact, 'Arial Narrow', sans-serif";
  g.fillText('NO CALLERS', 128, 46);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

/** The hut in its own space: centred on the origin, door wall facing +Z. Caller places and disposes it. */
export function buildHut(): { root: THREE.Group; dispose(): void } {
  const { width: W, depth: D, height: H } = HUT;
  const k = new RoomKit();
  const front = D / 2;
  const dX = HUT.doorX;
  // Brick courses (a shade each, so the walls read as brick from the street camera).
  const courses = 9;
  for (let i = 0; i < courses; i++) {
    const h = H / courses;
    k.box(W, h, D, 0, h * (i + 0.5), 0, i % 2 ? 0x55301f : 0x4c2b1d, 0.22);
  }
  // Corrugated iron roof, overhanging, rust in patches.
  const sheets = 16;
  for (let i = 0; i < sheets; i++) {
    const w = (W + 0.4) / sheets;
    const x = -(W + 0.4) / 2 + w * (i + 0.5);
    k.box(w * 0.98, 0.05, D + 0.5, x, H + 0.06 + (i % 2) * 0.03, 0.1, i % 5 === 2 ? 0x5a3a28 : i % 3 ? 0x4c4741 : 0x5c4232, 0.16);
  }
  // Door, frame, step.
  k.box(1.14, 2.1, 0.08, dX, 1.05, front + 0.03, 0x2a2018, 0.05);
  k.box(0.96, 1.98, 0.06, dX, 1.0, front + 0.07, 0x2c4130, 0.12);
  for (const y of [0.45, 1.55]) k.box(0.9, 0.08, 0.03, dX, y, front + 0.11, 0x24372a, 0.08);
  k.box(0.05, 0.12, 0.05, dX + 0.36, 1.05, front + 0.12, 0x8a8478, 0.05); // hasp and padlock
  k.box(1.4, 0.12, 0.45, dX, 0.06, front + 0.24, 0x6a665e, 0.1);
  // A boarded window to the right of the door.
  k.box(0.8, 0.6, 0.06, 0.6, 1.5, front + 0.03, 0x2a2018, 0.05);
  for (let i = 0; i < 3; i++) k.box(0.86, 0.15, 0.04, 0.6, 1.32 + i * 0.18, front + 0.07, 0x7a6a4c, 0.18);
  // Lamp bracket over the door.
  k.box(0.04, 0.04, 0.3, dX, 2.2, front + 0.15, 0x1a1a1a, 0);
  // Tyres and an oil drum against the left wall.
  for (let i = 0; i < 3; i++) k.cyl(0.33, 0.2, W / 2 + 0.4, 0.1 + i * 0.21, 0.6, 0x1c1b1a, 14, 0.1);
  k.cyl(0.28, 0.85, W / 2 + 0.35, 0.425, -0.5, 0x3d4a3a, 12, 0.15);
  const geo = k.build();
  const mat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.9 });
  const mesh = new THREE.Mesh(geo, mat);
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  const root = new THREE.Group();
  root.name = 'sid-hut';
  root.add(mesh);
  // The lamp: a warm glow (bright enough to bloom), no real light (the street's light count stays put).
  const bulbGeo = new THREE.SphereGeometry(0.07, 10, 6);
  const bulbMat = new THREE.MeshStandardMaterial({ color: 0x000000, emissive: 0xffc27a, emissiveIntensity: 5 });
  const bulb = new THREE.Mesh(bulbGeo, bulbMat);
  bulb.position.set(dX, 2.13, front + 0.3);
  root.add(bulb);
  const disposables: { dispose(): void }[] = [geo, mat, bulbGeo, bulbMat];
  const tex = signTexture();
  if (tex) {
    const sGeo = new THREE.PlaneGeometry(1.0, 0.25);
    const sMat = new THREE.MeshStandardMaterial({ map: tex, roughness: 0.95 });
    const sign = new THREE.Mesh(sGeo, sMat);
    sign.position.set(0.6, 2.2, front + 0.05);
    root.add(sign);
    disposables.push(sGeo, sMat, tex);
  }
  return {
    root,
    dispose() {
      root.removeFromParent();
      for (const d of disposables) d.dispose();
    },
  };
}
